import { ObjectCategory, ZIndexes } from "@common/constants";
import { type AnimalDefinition, AnimalKind, AnimalState, YetiPrints, animalOfKind } from "@common/definitions/animals";
import { CircleHitbox } from "@common/utils/hitbox";
import { type ObjectsNetData } from "@common/utils/objectsSerializations";
import { Numeric } from "@common/utils/math";
import { randomBoolean, randomFloat } from "@common/utils/random";
import { Vec, type Vector } from "@common/utils/vector";
import { GameConsole } from "../console/gameConsole";
import { Game } from "../game";
import { ATTIC_DARK_ZINDEX } from "../managers/atticDark";
import { ParticleManager } from "../managers/particleManager";
import { type GameSound, SoundManager } from "../managers/soundManager";
import { PIXI_SCALE, PLAYER_PARTICLE_ZINDEX } from "../utils/constants";
import { TrenchioSprite, toPixiCoords } from "../utils/pixi";
import { WarWeather } from "../war/warWeather";
import { GameObject } from "./gameObject";

/** how each kind is drawn: its picture (and the second of its gait), its carcass's, its size, its blood; where it's
 * drawn alive and where its blood is */
const LOOKS: Readonly<Record<AnimalKind, {
    readonly frames: readonly string[]
    readonly dead: string
    readonly scale: number
    readonly blood: number
    readonly zIndex: number
    readonly bloodZIndex: number
}>> = {
    [AnimalKind.Hare]: { frames: ["hare"], dead: "hare_dead", scale: 1.5, blood: 0xffffff, zIndex: ZIndexes.GroundedThrowables, bloodZIndex: PLAYER_PARTICLE_ZINDEX },
    // (its legs in the two halves of a spider's gait, swapped as it scuttles; its blood a sickly yellow green. It and
    // its blood are under the attic's dark, seen where a light falls: 1.04.1, the owner, 25 October 2026: "spider looks
    // too big, and doesnt fit with lightning really, its like its over the lightning")
    [AnimalKind.Spider]: {
        frames: ["attic_spider_1", "attic_spider_2"], dead: "attic_spider_dead", scale: 1, blood: 0x9aa83a,
        zIndex: ATTIC_DARK_ZINDEX - 0.25, bloodZIndex: ATTIC_DARK_ZINDEX - 0.2
    },
    // (0.98.4) the Mothman: perched on an eave with its wings folded, flying with them beating (three pictures of the
    // beat), standing on the ground; its blood black. Over the roofs when it's up there, among the players when down
    [AnimalKind.Mothman]: {
        frames: ["mothman_perch", "mothman_fly_1", "mothman_fly_2", "mothman_fly_3", "mothman_ground"], dead: "mothman_dead", scale: 1, blood: 0x1a1016,
        zIndex: ZIndexes.ObstaclesLayer5 + 0.5, bloodZIndex: PLAYER_PARTICLE_ZINDEX
    },
    // (0.98.4) the Yeti: the two halves of its lumbering gait, its roar; drawn over the players, as big as it is
    [AnimalKind.Yeti]: {
        frames: ["yeti_1", "yeti_2", "yeti_roar"], dead: "yeti_dead", scale: 1, blood: 0xffffff,
        zIndex: ZIndexes.ObstaclesLayer3 + 0.2, bloodZIndex: PLAYER_PARTICLE_ZINDEX
    },
    // (0.98.4) a street rat: two halves of its scurry, small, under everything that walks
    [AnimalKind.Rat]: {
        frames: ["rat_1", "rat_2"], dead: "rat_dead", scale: 1, blood: 0xffffff,
        zIndex: ZIndexes.GroundedThrowables, bloodZIndex: PLAYER_PARTICLE_ZINDEX
    }
};

/** the Mothman down on the ground: among the players, over them */
const MOTHMAN_GROUND_ZINDEX = ZIndexes.Players + 0.5;
/** the Mothman's flight as drawn: how far its body rises on the screen at the top of its flight (units), and how much bigger it's drawn */
const FLIGHT_LIFT = 6;
const FLIGHT_GROW = 0.35;
/** where its eyes are: this far ahead of its picture's middle (units; tools/creatures/creatures.py HEAD_AHEAD + 5 px) */
const MOTHMAN_EYES_AHEAD = 2.25;
/** its wings' beat: one picture of it every so many ms, quick as it takes off and comes down, slow as it glides high */
const BEAT_MS = { low: 70, high: 150 };
/** landing: it settles for this long (drawn a little big, then its size) */
const SETTLE_MS = 380;

/**
 * An animal (client side): the wild hare, one of the museum attic's spiders (1.04), or (0.98.4) the Mothman, the Yeti
 * or a street rat. The server moves it; this draws it, interpolates between updates and animates it while it moves: a
 * hare's squash-and-stretch hop, a spider's legs scuttling, the Mothman's wings beating as it flies (lifted and bigger
 * the higher, its shadow left on the ground), its red eyes burning, the Yeti's lumbering gait, its roar and the great
 * prints it leaves in the snow, a rat's scurry. Alive, a hare sits under tree canopies and a spider under the attic's
 * dark (seen where a light falls, as the boards it creeps on: 1.04.1); dead, the carcass lies on the ground beneath the
 * loot.
 */
export class Animal extends GameObject.derive(ObjectCategory.Animal) {
    readonly body: TrenchioSprite;
    readonly hitbox: CircleHitbox;
    override damageable = true;
    dead = false;
    definition: AnimalDefinition;
    private _state = AnimalState.Wander;
    private _lastPosition?: Vector;
    private _lastMove = 0;
    private _hop = 0;
    private _gait = 0;
    private _lastFrame = Date.now();
    private _lastRustle = 0;
    // ---- the Mothman's (0.98.4) ----
    /** how high it is in its flight, as the server says (0 on the ground or a roof) */
    height = 0;
    private _eyes?: TrenchioSprite;
    private _eyeGlow?: TrenchioSprite;
    private _shadow?: TrenchioSprite;
    private _wings?: GameSound;
    private _stretchUntil = 0;
    private _nextStretch = 0;
    private _settleUntil = 0;
    private _flutterUntil = 0;
    private _nextFlutter = 0;
    // ---- the Yeti's (0.98.4) ----
    private _strideLeft = 0;
    private _side = 1;
    private _lastRoar = 0;
    private _roarUntil = 0;
    private _lastStep = 0;
    private _lastSqueak = 0;

    constructor(id: number, data: ObjectsNetData[ObjectCategory.Animal]) {
        super(id);
        this.definition = animalOfKind(data.kind);
        const look = LOOKS[this.definition.kind];
        this.hitbox = new CircleHitbox(this.definition.radius);
        this.body = new TrenchioSprite(look.frames[0]);
        this.container.addChild(this.body);
        // (a hare about one player-width long, so it reads at a glance; a spider drawn at its size, about a player across
        // its legs)
        this.container.scale.set(look.scale);
        if (this.definition.kind === AnimalKind.Mothman) this._makeMothman();
        this.updateFromData(data, true);
    }

    private get _look(): (typeof LOOKS)[AnimalKind] {
        return LOOKS[this.definition.kind];
    }

    /** the Mothman's eyes, their glow and its shadow */
    private _makeMothman(): void {
        this._shadow = new TrenchioSprite("mothman_shadow");
        this._shadow.alpha = 0;
        this._shadow.zIndex = ZIndexes.Decals + 0.6;
        this._eyeGlow = new TrenchioSprite("_glow_");
        this._eyeGlow.blendMode = "add";
        this._eyeGlow.tint = 0xff2a1a;
        this._eyeGlow.alpha = 0.3;
        this._eyeGlow.scale.set(0.28);
        this._eyes = new TrenchioSprite("mothman_eyes");
        this._eyes.blendMode = "add";
        this.container.addChild(this._eyeGlow, this._eyes);
    }

    override updateFromData(data: ObjectsNetData[ObjectCategory.Animal], isNew = false): void {
        if (!this._lastPosition || Vec.squaredLen(Vec.sub(data.position, this._lastPosition)) > 1e-4) this._lastMove = Date.now();
        const moved = this._lastPosition ? Vec.len(Vec.sub(data.position, this._lastPosition)) : 0;
        this._lastPosition = data.position;
        this.position = data.position;
        this.hitbox.position = data.position;
        this.rotation = data.rotation;
        this.layer = data.layer;
        this.height = data.height;
        // (smoothing off: the body goes straight where the server says, as a player's does: 0.98.5's audit, every
        // creature stood frozen where it was first seen with it off, its hitbox wandering away)
        if (isNew || !GameConsole.getBuiltInCVar("cv_movement_smoothing")) {
            this.container.position.copyFrom(toPixiCoords(this.position));
            this.container.rotation = this.rotation;
        }

        const dead = data.state === AnimalState.Dead;
        this.dead = dead;
        if (dead && this._state !== AnimalState.Dead) {
            this.body.setFrame(this._look.dead);
            this.body.scale.set(1);
            this._wings?.stop();
            this._wings = undefined;
            if (this._eyes) this._eyes.visible = false;
            if (this._eyeGlow) this._eyeGlow.visible = false;
            if (this._shadow) this._shadow.visible = false;
            if (this.definition.kind === AnimalKind.Yeti && !isNew) this.playSound("yeti_die", { falloff: 0.3, maxRange: 200 });
            if (this.definition.kind === AnimalKind.Mothman && !isNew) this.playSound("mothman_die", { falloff: 0.3, maxRange: 200 });
        }
        // (in the air nothing hits it, as on the server: the Mothman's flight)
        this.damageable = !dead && data.state !== AnimalState.Fly;
        const flier = this.definition.kind === AnimalKind.Mothman;
        if (flier && !dead) this._mothmanState(data.state, isNew);
        this.container.zIndex = dead
            ? ZIndexes.DeadObstacles
            : flier && data.state !== AnimalState.Perch && data.state !== AnimalState.Fly
                ? MOTHMAN_GROUND_ZINDEX
                : this._look.zIndex;
        // the Yeti's prints (0.98.4): one each stride, left and right by turns, where its feet fall
        if (this.definition.kind === AnimalKind.Yeti && !dead && !isNew && moved > 0 && moved < 20) {
            this._strideLeft -= moved;
            if (this._strideLeft <= 0) {
                this._strideLeft = YetiPrints.stride;
                this._side = -this._side;
                const heading = this.rotation;
                const across = Vec(-Math.sin(heading), Math.cos(heading));
                WarWeather.bigPrint(Vec.add(this.position, Vec.scale(across, 1.4 * this._side)), heading, this._side);
                const now = Date.now();
                if (now - this._lastStep > 250) {
                    this._lastStep = now;
                    this.playSound("yeti_step", { falloff: 0.5, maxRange: 70, variation: { pitch: 0.08, volume: 0.1 } });
                }
            }
        }
        if (this.definition.kind === AnimalKind.Yeti && !dead && data.state === AnimalState.Alert && this._state !== AnimalState.Alert && !isNew) {
            this._roar();
        }
        this._state = data.state;
        this.updateLayer();
    }

    /** the Mothman's change of state: its screech and its wings as it takes off, its landing */
    private _mothmanState(state: AnimalState, isNew: boolean): void {
        const was = this._state;
        if (state === AnimalState.Fly && was !== AnimalState.Fly) {
            if (!isNew) this.playSound("mothman_screech", { falloff: 0.3, maxRange: 220, variation: { pitch: 0.06 } });
            this._wings?.stop();
            this._wings = SoundManager.play("mothman_wings", { position: this.position, loop: true, dynamic: true, falloff: 0.5, maxRange: 90, layer: this.layer });
        } else if (state !== AnimalState.Fly && was === AnimalState.Fly) {
            this._wings?.stop();
            this._wings = undefined;
            if (!isNew) {
                this.playSound("mothman_land", { falloff: 0.5, maxRange: 80 });
                this._settleUntil = Date.now() + SETTLE_MS;
            }
        }
        if (this._wings) this._wings.position = this.position;
    }

    /** the Yeti's roar: its picture of it for a moment, the sound, now and then while it watches */
    private _roar(): void {
        const now = Date.now();
        if (now - this._lastRoar < 4500) return;
        this._lastRoar = now;
        this._roarUntil = now + 1100;
        this.playSound("yeti_roar", { falloff: 0.3, maxRange: 240, variation: { pitch: 0.05 } });
    }

    override update(): void {
        // all motion comes from the server; its drawing goes with the smoothing's frame, or here with smoothing off
        if (!GameConsole.getBuiltInCVar("cv_movement_smoothing")) this._animate();
    }

    /** a bullet or a melee blow landed: a flesh-hit sound and a small burst of blood (bullets and melee call this) */
    hitEffect(position: Vector, angle: number, sound?: string): void {
        if (!Game.gameOver) {
            const kind = this.definition.kind;
            const own = kind === AnimalKind.Mothman ? "mothman_hurt" : kind === AnimalKind.Yeti ? "yeti_hurt" : kind === AnimalKind.Rat ? "rat_squeak" : undefined;
            SoundManager.play(sound ?? own ?? `player_hit_${randomBoolean() ? "1" : "2"}`, {
                position,
                falloff: 0.2,
                maxRange: 96,
                layer: this.layer
            });
        }
        ParticleManager.spawnParticle({
            frames: "blood_particle",
            zIndex: this._look.bloodZIndex,
            layer: this.layer,
            position,
            lifetime: 800,
            scale: { start: 0.35, end: 0.7 },
            alpha: { start: 1, end: 0 },
            speed: Vec.fromPolar(angle, randomFloat(0.4, 0.8)),
            ...(this._look.blood !== 0xffffff ? { tint: this._look.blood } : {})
        });
    }

    override updateDebugGraphics(): void { /* no debug outline */ }

    override updateInterpolation(): void {
        this.updateContainerPosition();
        this.updateContainerRotation();
        this._animate();
    }

    /** each frame's drawing: its gait, its wings, its eyes, its shadow */
    private _animate(): void {
        const now = Date.now();
        const frame = Math.min(now - this._lastFrame, 100);
        this._lastFrame = now;
        if (this._state === AnimalState.Dead) return;
        const moving = now - this._lastMove < 140;

        switch (this.definition.kind) {
            case AnimalKind.Spider: {
                // its legs: the two halves of its gait swapped as it goes, faster when it bolts; at rest, a leg's twitch now
                // and then
                const frames = this._look.frames;
                if (moving) {
                    this._gait += frame / (this._state === AnimalState.Flee ? 55 : 85);
                    this.body.setFrame(frames[Math.floor(this._gait) % frames.length]);
                    const s = Math.sin(this._gait * Math.PI);
                    this.body.scale.set(1 + 0.03 * s, 1 - 0.02 * s);
                } else {
                    if (Math.random() < frame / 2600) this._gait += 1;
                    this.body.setFrame(frames[Math.floor(this._gait) % frames.length]);
                    this.body.scale.set(1);
                }
                return;
            }
            case AnimalKind.Mothman:
                this._drawMothman(now, frame, moving);
                return;
            case AnimalKind.Yeti:
                this._drawYeti(now, frame, moving);
                return;
            case AnimalKind.Rat: {
                const frames = this._look.frames;
                if (moving) {
                    this._gait += frame / (this._state === AnimalState.Flee ? 45 : 70);
                    this.body.setFrame(frames[Math.floor(this._gait) % 2]);
                    const s = Math.sin(this._gait * Math.PI);
                    this.body.scale.set(1 + 0.05 * s, 1 - 0.04 * s);
                    const player = Game.activePlayer;
                    if (this._state === AnimalState.Flee && player && now - this._lastSqueak > 2200 && Vec.squaredLen(Vec.sub(this.position, player.position)) < 20 * 20) {
                        this._lastSqueak = now;
                        this.playSound("rat_squeak", { falloff: 0.5, maxRange: 40, variation: { pitch: 0.15 } });
                    }
                } else {
                    this.body.setFrame(frames[0]);
                    // (a sniff: its nose twitching)
                    this.body.scale.set(1 + 0.02 * Math.sin(now / 90), 1);
                }
                return;
            }
            default: break;
        }

        // hop: stretch forward on take-off, squash on landing; ease back to rest when it stops
        if (moving) {
            this._hop = (this._hop + frame / (this._state === AnimalState.Flee ? 240 : 360)) % 1;
            // a quiet rustle when it hops near you, so you notice it
            const player = Game.activePlayer;
            if (player && now - this._lastRustle > 1500 && Vec.squaredLen(Vec.sub(this.position, player.position)) < 24 * 24) {
                this._lastRustle = now;
                SoundManager.play(`bush_rustle_${randomBoolean() ? "1" : "2"}`, { position: this.position, falloff: 0.5, maxRange: 48, layer: this.layer });
            }
            const s = Math.sin(this._hop * Math.PI * 2);
            this.body.scale.set(1 + 0.13 * s, 1 - 0.09 * s);
        } else {
            this._hop = 0;
            this.body.scale.set(this.body.scale.x + (1 - this.body.scale.x) * 0.2, this.body.scale.y + (1 - this.body.scale.y) * 0.2);
        }
    }

    /**
     * the Mothman each frame: perched, still but for a breath and now and then a stretch of its wings; flying, its wings
     * beating, its body lifted up the screen and drawn bigger the higher it is, its shadow left on the ground under it;
     * down, standing, swaying a little, looking about. Its eyes burn red whatever it does, brighter in the dark
     */
    private _drawMothman(now: number, frame: number, moving: boolean): void {
        const frames = this._look.frames;
        const t = now / 1000;
        const height = this.height;
        const flying = this._state === AnimalState.Fly;
        // (landed: it settles, drawn a little big for a moment)
        const settle = this._settleUntil > now ? (this._settleUntil - now) / SETTLE_MS : 0;
        const settled = 1 + 0.1 * settle * settle;
        if (flying) {
            // (the beat: up, mid, down, mid; quick low down, taking off and coming in, a slow glide high up)
            this._gait += frame / Numeric.lerp(BEAT_MS.low, BEAT_MS.high, Numeric.clamp((height - 0.25) / 0.6, 0, 1));
            const beat = [1, 2, 3, 2][Math.floor(this._gait) % 4];
            this.body.setFrame(frames[beat]);
            this.body.scale.set(1 + FLIGHT_GROW * height);
            // (lifted up the screen and a little to the right, as everything high is, and a bob with each beat)
            const bob = Math.sin(this._gait * Math.PI / 2) * 0.12;
            this.body.position.set(2 * height * PIXI_SCALE, (-FLIGHT_LIFT * height + bob) * PIXI_SCALE);
            this.body.rotation = Math.sin(this._gait * Math.PI / 2) * 0.05;
        } else if (this._state === AnimalState.Perch) {
            if (now > this._nextStretch) {
                this._nextStretch = now + 6000 + Math.random() * 9000;
                this._stretchUntil = now + 550;
            }
            // (a stretch: its wings half out and back)
            this.body.setFrame(now < this._stretchUntil ? frames[(now < this._stretchUntil - 275 ? 1 : 2)] : frames[0]);
            // a breath
            this.body.scale.set(settled * (1 + 0.012 * Math.sin(t * 2.1)), settled * (1 + 0.02 * Math.sin(t * 2.1)));
            this.body.position.set(0, 0);
            // (its head turning: a glance either side, now and then)
            this.body.rotation = 0.08 * Math.sin(t * 0.7) * (Math.sin(t * 0.23) > 0.6 ? 1 : 0.2);
        } else {
            // down on the ground: standing and looking about, a shuffle of its steps as it walks, a flutter of its wings now and then
            if (now > this._nextFlutter) {
                this._nextFlutter = now + 2500 + Math.random() * 5000;
                this._flutterUntil = now + 220;
            }
            this.body.setFrame(now < this._flutterUntil ? frames[2] : frames[4]);
            const step = moving ? Math.sin(t * 9) : 0;
            this.body.scale.set(settled * (1 + 0.015 * Math.sin(t * 3) + 0.03 * step), settled * (1 - 0.015 * Math.sin(t * 3) - 0.02 * step));
            this.body.position.set(0, 0);
            this.body.rotation = moving ? 0.03 * step : 0.06 * Math.sin(t * 1.3);
        }
        // the eyes, with the body, on its head
        const eyes = this._eyes, glow = this._eyeGlow;
        if (eyes && glow) {
            const grow = this.body.scale.x;
            const head = Vec(MOTHMAN_EYES_AHEAD * grow * PIXI_SCALE, 0);
            const rotated = Vec.rotate(head, this.body.rotation);
            eyes.position.set(this.body.position.x + rotated.x, this.body.position.y + rotated.y);
            eyes.rotation = this.body.rotation;
            eyes.scale.set(grow);
            const pulse = 0.78 + 0.22 * Math.sin(t * 5.3);
            eyes.alpha = pulse;
            eyes.visible = true;
            glow.position.copyFrom(eyes.position);
            glow.scale.set(0.34 * grow * (1 + 0.15 * Math.sin(t * 5.3)));
            glow.alpha = 0.32 * pulse;
            glow.visible = true;
        }
        // its shadow on the ground, where it is, smaller and fainter the higher it flies (none perched: the roof's)
        const shadow = this._shadow;
        if (shadow) {
            if (!shadow.parent) this.container.parent?.addChild(shadow);
            shadow.visible = flying || this._state === AnimalState.Wander || this._state === AnimalState.Alert;
            shadow.position.copyFrom(this.container.position);
            shadow.rotation = this.container.rotation;
            shadow.scale.set((flying ? 1.05 - 0.4 * height : 0.72) * this.container.scale.x);
            shadow.alpha = flying ? 0.38 * (1 - 0.6 * height) : 0.3;
        }
        if (this._wings) this._wings.position = this.position;
    }

    /** the Yeti each frame: its gait, heavy, the two halves swapped each stride; its roar's picture; standing, a sway */
    private _drawYeti(now: number, frame: number, moving: boolean): void {
        const frames = this._look.frames;
        const t = now / 1000;
        if (now < this._roarUntil) {
            this.body.setFrame(frames[2]);
            const s = Math.sin((now - (this._roarUntil - 1100)) / 1100 * Math.PI);
            this.body.scale.set(1 + 0.06 * s, 1 + 0.08 * s);
            return;
        }
        if (moving) {
            this._gait += frame / (this._state === AnimalState.Flee ? 170 : 260);
            this.body.setFrame(frames[Math.floor(this._gait) % 2]);
            const s = Math.sin(this._gait * Math.PI);
            // (its weight coming down each step: a squash, and a roll from side to side)
            this.body.scale.set(1 + 0.04 * s, 1 - 0.035 * Math.abs(s));
            this.body.rotation = 0.05 * Math.sin(this._gait * Math.PI);
        } else {
            this.body.setFrame(frames[0]);
            this.body.scale.set(1 + 0.012 * Math.sin(t * 1.7), 1 + 0.018 * Math.sin(t * 1.7));
            this.body.rotation = 0.03 * Math.sin(t * 0.9);
            if (this._state === AnimalState.Alert && now - this._lastRoar > 5500 && Math.random() < frame / 4000) this._roar();
        }
    }

    override destroy(): void {
        super.destroy();
        this._wings?.stop();
        this._shadow?.destroy();
    }
}
